import { useCallback, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { toastManager, type EntitySelection } from "@salestrak/ui";
import { fieldDto } from "@salestrak/custom-fields";
import { CreateIssueDocument, type EntityType, type IssueRow } from "#app/lib/data";
import { insertIssueIntoStageCache } from "#app/hooks/kanban/kanban-cache-utils";
import { useRefetchIssueGroups } from "#app/hooks/issue-list/use-refetch-issue-groups";
import { isForbiddenError } from "#app/lib/utils/is-forbidden-error";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import type { CustomFieldDef } from "#app/components/issue-view/issue-view-types";
import { useAnalytics, AnalyticsContext, IssueEvent } from "#app/lib/analytics";
import { useAutomationPermissions } from "#app/hooks/automations/use-automation-permissions";
import { openAutomationRunsDialog } from "#app/lib/automation-runs-dialog-context";
import { watchAutomationTouch } from "#app/lib/automation-touches";
import { useAutomationsEnabled } from "#app/lib/hooks/use-automations-enabled";
import type { Omittable } from "@salestrak/ui";

type CreateIssueDraft = Omittable<{
  name: string;
  description?: string;
  issueTypeId: string | null;
  workflowId: string;
  stageId: string | null;
  issueSourceId?: string | null;
  address: Omittable<{ wholeAddress?: string; latLng?: { lat: number; lng: number } }> | null;
  assignees: EntitySelection[];
  // Teams property — attached on create via CreateIssueInput.teamIds.
  teams: EntitySelection[];
  values: Record<string, unknown>;
}>;

type UseCreateIssueMutationOptions = Omittable<{
  onSuccess?: (issue: IssueRow) => void;
  /** Where the create flow was launched from — included in analytics. */
  source?: string;
}>;

export function useCreateIssueMutation({ onSuccess, source }: UseCreateIssueMutationOptions = {}) {
  const [createIssueMutation] = useMutation(CreateIssueDocument);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { track } = useAnalytics();
  const refetchIssueGroups = useRefetchIssueGroups();
  const automationsEnabled = useAutomationsEnabled();
  const { canViewRuns } = useAutomationPermissions();
  const offersRuns = automationsEnabled && canViewRuns;

  const createIssue = useCallback(
    async (draft: CreateIssueDraft, customFieldDefs: Record<string, CustomFieldDef>) => {
      if (!draft.name.trim()) return;

      setIsSubmitting(true);
      try {
        // Normalize custom field values through fieldDto
        const normalizedValues: Record<string, unknown> = {};
        for (const [cfId, value] of Object.entries(draft.values)) {
          const field = customFieldDefs[cfId];
          if (!field || value === undefined || value === null) continue;
          try {
            normalizedValues[cfId] = fieldDto(field.type, value);
          } catch {
            normalizedValues[cfId] = value;
          }
        }

        const input = {
          name: draft.name.trim(),
          description: draft.description || undefined,
          issueTypeId: draft.issueTypeId || undefined,
          stageId: draft.stageId || undefined,
          workflowId: draft.workflowId,
          issueSourceId: draft.issueSourceId || undefined,
          address: draft.address?.wholeAddress || draft.address?.latLng
            ? {
                wholeAddress: draft.address?.wholeAddress || undefined,
                latLng: draft.address?.latLng ?? undefined,
              }
            : undefined,
          assignees:
            draft.assignees.length > 0
              ? draft.assignees.map((s, index) => ({
                  entityType: ((s.entity.data?.entityType as string) ?? "user") as EntityType,
                  entityId: s.id,
                  isPrimary: index === 0,
                }))
              : undefined,
          teamIds:
            draft.teams.length > 0 ? draft.teams.map((s) => s.id) : undefined,
          values:
            Object.keys(normalizedValues).length > 0
              ? normalizedValues
              : undefined,
        };

        const { data, error } = await createIssueMutation({
          variables: { input },
          update: (cache, { data: result }) => {
            if (!result?.createIssue) return;
            const created = result.createIssue;
            if ("issueId" in created && created.issueId && draft.stageId) {
              insertIssueIntoStageCache(cache, draft.stageId, {
                issueId: created.issueId as string,
                sortOrder: Number.MIN_SAFE_INTEGER,
              });
            }
          },
        });

        // Apollo client uses errorPolicy: "all" globally, so top-
        // level GraphQL errors land in the result `error` instead of throwing.
        // The global error link already toasts for 403/forbidden;
        // skip the generic failure toast in that case.
        if (!data?.createIssue) {
          if (!isForbiddenError(error)) {
            toastManager.add({ title: "Failed to create Project", type: "error" });
          }
          return;
        }

        const result = data.createIssue;
        if ("__typename" in result) {
          if (result.__typename === "ValidationError" || result.__typename === "PermissionDenied") {
            toastManager.add({ title: "message" in result ? (result.message as string) : "Failed to create Project", type: "error" });
            return;
          }
        }

        const toastId = toastManager.add({ title: "Project created", type: "success" });
        if ("issueId" in result && typeof result.issueId === "string") {
          // An automation that fires on create can move or edit the new
          // project before, or just after, this answer lands. The one toast
          // says so in place — a second toast for the same moment is noise.
          const issueId = result.issueId;
          watchAutomationTouch(issueId, (touch) => {
            toastManager.update(toastId, {
              description: touch.kind === "moved" ? "Moved by automation" : "Updated by automation",
              ...(offersRuns
                ? {
                    actionProps: {
                      children: "View run",
                      onClick: () => {
                        toastManager.close(toastId);
                        openAutomationRunsDialog({ issueId, issueName: draft.name }, "create_toast");
                      },
                    },
                  }
                : {}),
            });
          });
        }
        // The stage-cache insert above only patches `issues` cache entries that
        // already exist — a group holding zero projects was never activated (no
        // query, no entry), and non-stage groupings (assignee, type, age) have
        // no writer at all. Refetching the group counts + active issues queries
        // is what surfaces the new project in those groups; fire-and-forget so
        // the create flow does not wait on it.
        void refetchIssueGroups();
        if ("issueId" in result) {
          track({
            context: AnalyticsContext.Issue,
            event: IssueEvent.CreateFinish,
            data: {
              source: source ?? "unknown",
              teamCount: draft.teams.length,
              assigneeCount: draft.assignees.length,
              fieldCount: Object.keys(normalizedValues).length,
            },
          });
          onSuccess?.(result as unknown as IssueRow);
        }
      } catch (err) {
        toastMutationError(err, "Failed to create Project");
      } finally {
        setIsSubmitting(false);
      }
    },
    [createIssueMutation, onSuccess, source, track, refetchIssueGroups, offersRuns],
  );

  return { createIssue, isSubmitting };
}
